/**
 * The Control rect solver's per-type registry (ADR-0002, through
 * `createTypeRegistry`). Each slice registers its solver on import. An
 * unregistered type is a leaf: minimum size `(0, 0)`, and its children solve
 * as anchored Controls against its rect. No React, no THREE.
 */
import { createTypeRegistry } from '../../../core/createTypeRegistry';
/** The request for a themed icon: its ref, in its theme's scope, held as its theme holds it. */
export function themedSlotRequest(key, themed) {
    return { key, ref: themed.ref, scope: themed.resources, isHeldLocally: themed.isHeldLocally };
}
class ControlSolverRegistry {
    minimumSizeFns = createTypeRegistry('controlSolverRegistry.minimumSize');
    containerLayoutFns = createTypeRegistry('controlSolverRegistry.containerLayout');
    textureSlotFns = createTypeRegistry('controlSolverRegistry.textureSlots');
    childVisibilityFns = createTypeRegistry('controlSolverRegistry.childVisibility');
    canvasBoundaryTypes = new Set();
    sizeDependentMinimumTypes = new Set();
    /**
     * Declares a type in the Control walk that is not a `CanvasItem`, such as
     * `CanvasLayer`. Its Controls anchor against the viewport, as
     * `Control::get_parent_anchorable_rect` does, not a degenerate `(0, 0)` rect.
     * Registry data, not a type comparison in the solver, like every per-type behaviour.
     */
    registerCanvasBoundary(typeName) {
        this.canvasBoundaryTypes.add(typeName);
    }
    /** Whether this type is a non-CanvasItem canvas host (see `registerCanvasBoundary`). */
    isCanvasBoundary(typeName) {
        return this.canvasBoundaryTypes.has(typeName);
    }
    registerMinimumSize(typeName, fn) {
        this.minimumSizeFns.register(typeName, fn);
    }
    /**
     * Declares a type whose `MinimumSizeFn` reads `SolveContext.tentativeRect`.
     * A set, so `solveControlTree` decides on a second pass with a membership
     * test during `assignPaintIndex` rather than a traversal of its own.
     */
    registerSizeDependentMinimum(typeName) {
        this.sizeDependentMinimumTypes.add(typeName);
    }
    /** Whether this type's `MinimumSizeFn` reads `SolveContext.tentativeRect` (see `registerSizeDependentMinimum`). */
    isSizeDependentMinimum(typeName) {
        return this.sizeDependentMinimumTypes.has(typeName);
    }
    registerContainerLayout(typeName, fn) {
        this.containerLayoutFns.register(typeName, fn);
    }
    registerTextureSlots(typeName, fn) {
        this.textureSlotFns.register(typeName, fn);
    }
    /** Declares a container that writes its children's `visible` (see `ChildVisibilityFn`). */
    registerChildVisibility(typeName, fn) {
        this.childVisibilityFns.register(typeName, fn);
    }
    /**
     * This type's `ChildVisibilityFn`, or `undefined`. Returned, not invoked: the
     * caller enumerates the sortable children first, and only for a registered type.
     */
    childVisibility(typeName) {
        return this.childVisibilityFns.get(typeName);
    }
    /** This type's `TextureSlotsFn`, or `undefined` for a type that keeps the single-slot fallback. */
    textureSlots(typeName) {
        return this.textureSlotFns.get(typeName);
    }
    minimumSize(typeName) {
        return this.minimumSizeFns.get(typeName);
    }
    containerLayout(typeName) {
        return this.containerLayoutFns.get(typeName);
    }
    /** Test-only: drops every registration between test files. */
    clear() {
        this.minimumSizeFns.clear();
        this.containerLayoutFns.clear();
        this.textureSlotFns.clear();
        this.childVisibilityFns.clear();
        this.canvasBoundaryTypes.clear();
        this.sizeDependentMinimumTypes.clear();
    }
}
export const controlSolverRegistry = new ControlSolverRegistry();
//# sourceMappingURL=solverRegistry.js.map